import { ComponentType } from 'react';
import { Row } from '@tanstack/react-table';
import { RightArrowIcon } from '@entur/icons';
import classNames from 'classnames/bind';
import { Action, DrawerConfig, HighlightedItemType } from '../types';
import { type CleosTableFeaturesType } from '../tableFeatures';
import { TableCell } from './TableCell';
import { RowActions } from './RowActions';
import styles from '../styles.module.scss';

const cx = classNames.bind(styles);

const PARAM_TYPES = ['UsageParameter', 'UserProfile', 'GroupTicket', 'GenericParam'];

interface TableRowProps<TData extends object> {
  row: Row<CleosTableFeaturesType, TData>;
  expandable?: boolean;
  expandedContent?: ComponentType<{ content: TData; expandNextRow?: () => void }>;
  isExpanded: boolean;
  onExpandToggle: () => void;
  onExpandNext: () => void;
  actions?: Action<TData>[];
  highlightedRow?: HighlightedItemType;
  hasColumnSelector?: boolean;
  openDrawer: (row: TData, drawer: DrawerConfig, index?: number) => void;
}

export const TableRow = <TData extends object>({
  row,
  expandable,
  expandedContent,
  isExpanded,
  onExpandToggle,
  onExpandNext,
  actions,
  highlightedRow,
  hasColumnSelector,
  openDrawer,
}: TableRowProps<TData>) => {
  const original = row.original;
  const typedOriginal = original as Record<string, unknown>;
  const depth = (typedOriginal['depth'] as number | undefined) ?? null;
  const rowType = PARAM_TYPES.includes(typedOriginal['type'] as string);

  const isHighlighted = highlightedRow
    ? highlightedRow.includes
      ? String(typedOriginal[highlightedRow.key] ?? '').includes(String(highlightedRow.value))
      : typedOriginal[highlightedRow.key] === highlightedRow.value
    : false;

  const rowClasses = cx({
    tr: true,
    expandable,
    highlighted: isHighlighted,
    isActive: isExpanded,
  });

  const visibleCells = row.getVisibleCells();
  const contentColSpan = visibleCells.length + 1 + (actions || hasColumnSelector ? 1 : 0);
  const ExpandedContent = expandedContent;

  return (
    <>
      <tr id={row.id} data-rowid={row.id} className={rowClasses}>
        {expandable && (
          <td
            className={styles.expandableArrowCell}
            onClick={(e) => {
              e.stopPropagation();
              onExpandToggle();
            }}
          >
            <RightArrowIcon
              size={16}
              style={{ color: 'var(--cleos-text)' }}
              tabIndex={0}
              role="button"
              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onExpandToggle();
                  e.preventDefault();
                }
              }}
            />
          </td>
        )}

        {visibleCells.map((cell, cellIndex) => (
          <TableCell
            key={cell.id}
            cell={cell}
            cellIndex={cellIndex}
            original={original}
            depth={depth}
            rowType={rowType}
            expandable={expandable}
            onExpandToggle={onExpandToggle}
            openDrawer={openDrawer}
          />
        ))}

        {hasColumnSelector && !actions && <td className={styles.actions} />}
        {actions && <RowActions row={original} actions={actions} />}
      </tr>

      {expandable && isExpanded && (
        <tr className={rowClasses}>
          <td colSpan={contentColSpan} className={cx('td', 'expanded-component')}>
            {ExpandedContent && <ExpandedContent content={original} expandNextRow={onExpandNext} />}
          </td>
        </tr>
      )}
    </>
  );
};
